import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { subscribeNewsletter } from '../../util/api';
import Field, { hasDataInFields } from '../PageBuilder/Field';
import BlockBuilder from '../PageBuilder/BlockBuilder/BlockBuilder';
import SectionContainer from '../PageBuilder/SectionBuilder/SectionContainer';
import {
  IconLink,
  IconSocialFacebook,
  IconSocialLinkedin,
  IconSocialX,
} from '../../components';

import css from './SectionArticleBlog.module.css';

/**
 * Calculate estimated reading time based on word count
 * Average reading speed: 200 words per minute
 */
const calculateReadTime = (blocks = []) => {
  let totalWords = 0;

  const countWordsInContent = (content) => {
    if (typeof content === 'string') {
      return content.trim().split(/\s+/).filter(word => word.length > 0).length;
    }
    return 0;
  };

  const processField = (field) => {
    if (!field) return;

    if (field.content) {
      totalWords += countWordsInContent(field.content);
    }

    // Handle nested content in blocks
    if (field.text) {
      totalWords += countWordsInContent(field.text.content);
    }
  };

  blocks.forEach(block => {
    // Process all fields in the block
    Object.values(block).forEach(value => {
      if (value && typeof value === 'object') {
        processField(value);
      }
    });
  });

  const minutes = Math.ceil(totalWords / 200);
  return minutes || 1; // Minimum 1 minute
};

/**
 * Custom section component for blog articles with sidebar
 * Includes share buttons and newsletter subscription
 */
const SectionArticleBlog = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    blocks = [],
    isInsideContainer = false,
    options,
  } = props;

  const [copySuccess, setCopySuccess] = useState(false);
  const [showNameField, setShowNameField] = useState(false);
  // 'idle' | 'submitting' | 'success' | 'error'
  const [subscribeStatus, setSubscribeStatus] = useState('idle');

  const fieldOptions = options?.fieldComponents;
  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const hasBlocks = blocks && blocks.length > 0;

  // Get current page URL for sharing
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const pageTitle = typeof document !== 'undefined' ? document.title : '';

  // Calculate read time from blocks
  const readTime = calculateReadTime(blocks);

  // Article metadata
  const authorId = '69aaf938-c2de-43f9-a8e3-2c001864a85c';
  const authorName = 'repoze supply';
  const publishDate = '5th March 2026'; // TODO: This should come from CMS metadata when available

  const getCookie = name => {
    const parts = `; ${document.cookie}`.split(`; ${name}=`);
    return parts.length === 2 ? decodeURIComponent(parts.pop().split(';').shift()) : '';
  };

  // Newsletter signup → our server → Campaign Monitor (server/api/newsletter.js)
  const handleSubscribe = e => {
    e.preventDefault();
    const field = name => e.currentTarget.elements.namedItem(name);
    setSubscribeStatus('submitting');
    subscribeNewsletter({
      email: field('email').value,
      name: field('name').value,
      acceptTerms: field('acceptTerms').checked,
      website: field('website').value,
      utmSource: getCookie('utm_source'),
      utmMedium: getCookie('utm_medium'),
      utmCampaign: getCookie('utm_campaign'),
      utmContent: getCookie('utm_content'),
      utmId: getCookie('utm_id'),
    })
      .then(() => setSubscribeStatus('success'))
      .catch(() => setSubscribeStatus('error'));
  };

  // Handle email input to show name field
  const handleEmailInput = (e) => {
    if (e.target.value.length > 0 && !showNameField) {
      setShowNameField(true);
    }
  };

  // Share handlers
  const handleCopyLink = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(currentUrl).then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      });
    }
  };

  const handleFacebookShare = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`;
    window.open(facebookUrl, '_blank', 'width=600,height=400');
  };

  const handleLinkedInShare = () => {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`;
    window.open(linkedInUrl, '_blank', 'width=600,height=400');
  };

  const handleTwitterShare = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(pageTitle)}`;
    window.open(twitterUrl, '_blank', 'width=600,height=400');
  };

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.blogArticleLayout}>
        {/* Main Content Column */}
        <div className={css.mainContent}>
          {hasHeaderFields ? (
            <header className={classNames(defaultClasses.sectionDetails, css.articleHeader)}>
              {/* Article Metadata */}
              <div className={css.articleMeta}>
                <span className={css.category}>Founder Story</span>
                <span className={css.readTime}>{readTime} min read</span>
              </div>

              {/* Article Title */}
              <Field data={title} className={classNames(defaultClasses.title, css.articleTitle)} options={fieldOptions} />

              {/* Author and Date */}
              <div className={css.authorInfo}>
                <a
                  href={`/u/${authorId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={css.authorLink}
                >
                  {authorName}
                </a>
                <span className={css.separator}>|</span>
                <span className={css.publishDate}>{publishDate}</span>
              </div>

              <Field data={description} className={defaultClasses.description} options={fieldOptions} />
              <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
            </header>
          ) : null}
          {hasBlocks ? (
            <div
              className={classNames(defaultClasses.blockContainer, css.articleMain, {
                [css.noSidePaddings]: isInsideContainer,
              })}
            >
              <BlockBuilder
                blocks={blocks}
                sectionId={sectionId}
                ctaButtonClass={defaultClasses.ctaButton}
                options={options}
              />
            </div>
          ) : null}
        </div>

        {/* Sidebar Column */}
        <aside className={css.sidebar}>
          {/* Share Section */}
          <div className={css.shareSection}>
            <h3 className={css.sidebarTitle}>Share the post</h3>
            <div className={css.shareButtons}>
              <div className={css.shareButtonWrapper}>
                <button
                  onClick={handleCopyLink}
                  className={css.shareButton}
                  aria-label="Copy link"
                  title="Copy link"
                >
                  <IconLink className={css.shareIcon} />
                </button>
                {copySuccess && (
                  <div className={css.copySuccessTooltip}>
                    <FormattedMessage id="SectionArticleBlog.linkCopied" />
                  </div>
                )}
              </div>
              <button
                onClick={handleFacebookShare}
                className={css.shareButton}
                aria-label="Share on Facebook"
                title="Share on Facebook"
              >
                <IconSocialFacebook className={css.shareIcon} />
              </button>
              <button
                onClick={handleLinkedInShare}
                className={css.shareButton}
                aria-label="Share on LinkedIn"
                title="Share on LinkedIn"
              >
                <IconSocialLinkedin className={css.shareIcon} />
              </button>
              <button
                onClick={handleTwitterShare}
                className={css.shareButton}
                aria-label="Share on X (Twitter)"
                title="Share on X"
              >
                <IconSocialX className={css.shareIcon} />
              </button>
            </div>
          </div>

          {/* Newsletter Section */}
          <div className={css.newsletterSection}>
            <h3 className={css.sidebarTitle}>Subscribe to Repogue</h3>
            <p className={css.newsletterDescription}>
              Stories about circular living, family life and the thinking behind repoze, delivered
              to your inbox.
            </p>

            {subscribeStatus === 'success' ? (
              <p className={css.newsletterDescription}>Thanks for subscribing!</p>
            ) : (
            <form className={css.newsletterForm} id="subForm" onSubmit={handleSubscribe}>
              {/* Email Field - Always visible */}
              <div className={css.fieldWrapper}>
                <label htmlFor="fieldEmail" className={css.visuallyHidden}>
                  Email
                </label>
                <input
                  autoComplete="email"
                  className={css.newsletterInput}
                  id="fieldEmail"
                  maxLength="200"
                  name="email"
                  placeholder="Email"
                  required
                  type="email"
                  onInput={handleEmailInput}
                />
              </div>

              {/* Name Field - Animated reveal */}
              <div className={classNames(css.fieldWrapper, css.nameFieldWrapper, {
                [css.nameFieldVisible]: showNameField,
              })}>
                <label htmlFor="fieldName" className={css.visuallyHidden}>
                  Name
                </label>
                <input
                  id="fieldName"
                  maxLength="200"
                  name="name"
                  placeholder="Your name"
                  className={css.newsletterInput}
                />
              </div>

              {/* Honeypot — hidden from people, bots fill it */}
              <input
                type="text"
                name="website"
                tabIndex="-1"
                autoComplete="off"
                className={css.visuallyHidden}
                aria-hidden="true"
              />

              {/* Submit Button */}
              <button
                type="submit"
                className={css.newsletterButton}
                disabled={subscribeStatus === 'submitting'}
              >
                Next
              </button>
              {subscribeStatus === 'error' ? (
                <p className={css.newsletterDescription}>Something went wrong. Please try again.</p>
              ) : null}

              {/* Terms Checkbox */}
              <div className={css.termsCheckbox}>
                <input
                  type="checkbox"
                  id="newsletter-terms"
                  name="acceptTerms"
                  required
                />
                <label htmlFor="newsletter-terms" className={css.termsLabel}>
                  I accept the{' '}
                  <a href="/p/terms-of-service" className={css.termsLink}>
                    Terms of Service
                  </a>{' '}
                  and the{' '}
                  <a href="/p/privacy-policy" className={css.termsLink}>
                    Privacy Policy
                  </a>
                </label>
              </div>
            </form>
            )}
          </div>
        </aside>
      </div>
    </SectionContainer>
  );
};

export default SectionArticleBlog;

